import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'https://saanjh.cc';

const QRCodePage = () => {
  const { vendor } = useAuth();

  if (!vendor) return null;

  const storefrontUrl = `${FRONTEND_URL}/store/${vendor.slug}`;

  const handleDownload = () => {
    if (!vendor.qr_code) return;

    const link = document.createElement('a');
    link.href = vendor.qr_code;
    link.download = `${vendor.slug}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR Code downloaded');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(storefrontUrl);
    toast.success('Link copied to clipboard');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vendor.business_name} - Store`,
          text: `Check out my store: ${vendor.business_name}`,
          url: storefrontUrl,
        });
        toast.success('Shared successfully');
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div data-testid="qr-code-page">
      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight">Your QR Code</h2>
        <p className="text-muted-foreground mt-2">Download and share your store's QR code</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Code Display */}
        <Card>
          <CardHeader>
            <CardTitle>QR Code</CardTitle>
            <CardDescription>Customers can scan this to visit your store</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            {vendor.qr_code ? (
              <>
                <div className="bg-white p-6 rounded-lg shadow-md mb-4">
                  <img
                    src={vendor.qr_code}
                    alt="Store QR Code"
                    className="w-64 h-64"
                    data-testid="qr-code-image"
                  />
                </div>
                <p className="text-sm text-muted-foreground text-center mb-4">
                  {vendor.business_name}
                </p>
                <Button onClick={handleDownload} className="w-full" data-testid="download-qr-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Download QR Code
                </Button>
              </>
            ) : (
              <p className="text-muted-foreground">QR code not available</p>
            )}
          </CardContent>
        </Card>

        {/* Store Link & Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Store Link</CardTitle>
            <CardDescription>Share your store URL directly</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-secondary rounded-md">
              <p className="text-sm font-medium mb-2">Your Store URL:</p>
              <p className="font-mono text-sm break-all text-muted-foreground">{storefrontUrl}</p>
            </div>

            <div className="space-y-2">
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="w-full"
                data-testid="copy-link-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Store Link
              </Button>

              <Button
                onClick={handleShare}
                variant="outline"
                className="w-full"
                data-testid="share-btn"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share Store
              </Button>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">How to Use Your QR Code</h4>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Download and print your QR code</li>
                <li>• Display it at your store, stall, or booth</li>
                <li>• Add it to your business cards or flyers</li>
                <li>• Share it on social media</li>
                <li>• Customers can scan to browse and order</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tips Card */}
      <Card className="mt-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="text-2xl">💡</div>
            <div>
              <h3 className="font-semibold mb-2 text-purple-900">Pro Tips</h3>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Place your QR code at eye level for easy scanning</li>
                <li>• Make sure the QR code is at least 2x2 inches when printed</li>
                <li>• Test the QR code after printing to ensure it works</li>
                <li>• Include a call-to-action like "Scan to Order" near the QR code</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QRCodePage;